// AccentMark - Positioned SVG stress mark component
// Renders a single stress mark using SVG <use> referencing StressMarkSymbols
// Dimensions from design_tokens.json: 8x10 viewBox
// Positioning: StressOverlayPlugin calculates y using boundingRectOffset token (-5px)

import { memo } from 'react'

interface AccentMarkProps {
  /** Mark type: 1=primary, 2=secondary, 0=unstressed */
  stressLevel: number
  /** X position relative to editor root */
  x: number
  /** Y position relative to editor root */
  y: number
  /** Syllable text (for aria-label) */
  syllable: string
  /** Whether this mark was manually overridden */
  overridden?: boolean
  /** Node key for interaction plugin */
  nodeKey: string
  /** Word this syllable belongs to */
  word: string
  /** Index of syllable within word */
  syllableIndex: number
}

/**
 * Custom comparison to prevent unnecessary re-renders
 *
 * Main play: Only re-render when actual props change
 * Stress marks update frequently during scroll/typing - prevent wasteful re-renders
 */
function arePropsEqual(prev: AccentMarkProps, next: AccentMarkProps): boolean {
  return (
    prev.stressLevel === next.stressLevel &&
    prev.x === next.x &&
    prev.y === next.y &&
    prev.syllable === next.syllable &&
    prev.overridden === next.overridden &&
    prev.nodeKey === next.nodeKey &&
    prev.word === next.word &&
    prev.syllableIndex === next.syllableIndex
  )
}

/**
 * AccentMark - SVG-based stress mark component
 *
 * Epic 04 Scope 06: SVG-only rendering (feature flag removed)
 *
 * Design token reference: docs/design/feedback_system/design_tokens.json
 * - Size: components.accentMark.size (8x10 viewBox)
 * - Positioning: components.accentMark.position.boundingRectOffset (-5px, applied by StressOverlayPlugin)
 * - Colors: components.accentMark.colors (success-600, warm-600, neutral-400)
 *
 * Note: The design token also defines a CSS `top: -17px` for style guide examples,
 * but actual rendering uses boundingRectOffset since we position relative to bounding rects.
 *
 * Benefits:
 * - Crisp rendering at any zoom level
 * - Consistent cross-browser appearance
 * - Better visual control
 *
 * Critical requirements:
 * - MUST preserve all data attributes for StressInteractionPlugin
 * - MUST keep pointer-events: none to allow clicks through to text
 */
const AccentMark = memo(function AccentMark({
  stressLevel,
  x,
  y,
  syllable,
  overridden,
  nodeKey,
  word,
  syllableIndex,
}: AccentMarkProps) {
  // Determine symbol ID and CSS class based on stress level
  let symbolId: string
  let className = 'stress-mark stress-mark--svg'

  if (stressLevel === 1) {
    symbolId = 'stress-mark-primary'
    className += ' stress-mark--primary'
  } else if (stressLevel === 2) {
    symbolId = 'stress-mark-secondary'
    className += ' stress-mark--secondary'
  } else {
    symbolId = 'stress-mark-unstressed'
    className += ' stress-mark--unstressed'
  }

  if (overridden) {
    className += ' stress-mark--overridden'
  }

  return (
    <svg
      className={className}
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: 8,  // Design token: accentMark.size.width
        height: 10,  // Design token: accentMark.size.height
        pointerEvents: 'none',
      }}
      aria-label={`${stressLevel === 1 ? 'Primary' : stressLevel === 2 ? 'Secondary' : 'Unstressed'} stress on "${syllable}"`}
      // Epic 07: Data attributes for StressInteractionPlugin context menu
      data-syllable-nodekey={nodeKey}
      data-syllable-word={word}
      data-syllable-index={syllableIndex}
      data-syllable-level={stressLevel}
    >
      <use href={`#${symbolId}`} />
    </svg>
  )
}, arePropsEqual)

export default AccentMark
